import {
    Avatar,
    HStack,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Tag,
    TagLabel,
    Text,
    VStack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { translate } from '../../../i18n';
import { GameParticipantDto } from '../../../models/game/dto/game.dto';
import { UserDto } from '../../../models/user/dto/user.dto';

interface CoveredByChipsProps {
    participants: GameParticipantDto[];
}

interface PayerGroup {
    payer: UserDto;
    players: UserDto[];
}

/**
 * Renders one expandable chip per payer that has covered other players in this
 * game (pay-for-others). Clicking a chip opens a popover listing the covered
 * participants with their avatars. Renders nothing if no participant has been
 * paid for by someone else.
 */
const CoveredByChips: React.FC<CoveredByChipsProps> = ({ participants }) => {
    const groups = useMemo<PayerGroup[]>(() => {
        const byPayer = new Map<string, PayerGroup>();
        for (const p of participants) {
            if (!p.paidBy || p.paidBy.id === p.participant.id) continue;
            const key = p.paidBy.id;
            const existing = byPayer.get(key);
            if (existing) {
                existing.players.push(p.participant);
            } else {
                byPayer.set(key, { payer: p.paidBy, players: [p.participant] });
            }
        }
        return Array.from(byPayer.values());
    }, [participants]);

    if (groups.length === 0) return null;

    return (
        <Wrap spacing={2} data-testid="covered-by-chips">
            {groups.map(({ payer, players }) => {
                const payerLabel = formatPayerLabel(payer);
                return (
                    <WrapItem key={payer.id}>
                        <Popover placement="bottom-start" isLazy>
                            <PopoverTrigger>
                                <Tag
                                    size="md"
                                    variant="subtle"
                                    colorScheme="purple"
                                    cursor="pointer"
                                    borderRadius="full"
                                    data-testid={`covered-by-chip-${payer.id}`}
                                >
                                    <Avatar
                                        size="2xs"
                                        name={payerLabel}
                                        src={payer.avatar}
                                        ml={-1}
                                        mr={2}
                                    />
                                    <TagLabel>
                                        {translate('game.covered_by', {
                                            count: players.length,
                                            payer: payerLabel,
                                        })}
                                    </TagLabel>
                                </Tag>
                            </PopoverTrigger>
                            <PopoverContent width="auto" minW="240px">
                                <PopoverArrow />
                                <PopoverHeader fontWeight="semibold" fontSize="sm">
                                    {translate('game.covered_players')}
                                </PopoverHeader>
                                <PopoverBody>
                                    <VStack align="stretch" spacing={2}>
                                        {players.map((player) => (
                                            <HStack key={player.id} spacing={2}>
                                                <Avatar
                                                    size="xs"
                                                    name={`${player.firstName} ${player.lastName}`}
                                                    src={player.avatar}
                                                />
                                                <Text fontSize="sm">
                                                    {player.firstName} {player.lastName}
                                                </Text>
                                            </HStack>
                                        ))}
                                    </VStack>
                                </PopoverBody>
                            </PopoverContent>
                        </Popover>
                    </WrapItem>
                );
            })}
        </Wrap>
    );
};

const formatPayerLabel = (user: UserDto): string => {
    const initial = user.lastName ? `${user.lastName.charAt(0)}.` : '';
    return [user.firstName, initial].filter(Boolean).join(' ').trim();
};

export default CoveredByChips;
